import { useState } from 'react'
import { ArrowRightLeft, Check, Settings } from 'lucide-react'
import { useQuoteApi } from 'src/api/db/apiQuote'
import { useOrderNotifications } from 'src/api/server/useOrderNotifications'
import { Button } from 'src/shared/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'src/shared/components/ui/dropdown-menu'
import { cn } from 'src/shared/components/ui/utils'
import {
  ADMIN_QUOTE_STATUS_GROUPS,
  DELIVERED_STATUS,
  SHIPPING_QUOTE_STATUS,
  effectiveQuoteStatus,
  getQuoteStatusOption,
} from 'src/shared/consts/quoteStatus'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { useToast } from 'src/shared/hooks/use-toast'
import { useRequestContext } from '../../hooks/RequestContext'
import { ChangeOwnershipDialog } from './ChangeOwnershipDialog'

const CUSTOMER_SHIPPING_NOTIFY_STATUSES = new Set([
  SHIPPING_QUOTE_STATUS,
  DELIVERED_STATUS,
])

export const OrderStatusSettingsMenu = ({
  status,
  onUpdated,
}: {
  status?: string | null
  onUpdated?: (status: string) => void
}) => {
  const { t } = useLocalization()
  const { toast } = useToast()
  const { quote, publicId, refetchQuote } = useRequestContext()
  const { updateQuote } = useQuoteApi({ skipListQuery: true })
  const orderNotifications = useOrderNotifications()
  const current = effectiveQuoteStatus(status ?? quote?.status)
  const [ownershipOpen, setOwnershipOpen] = useState(false)

  if (quote?.id == null) return null

  const handleChange = async (next: string) => {
    if (next === current) return
    try {
      const result = await updateQuote.mutateAsync({
        id: quote.id,
        status: next,
      })
      if (result.error) {
        throw new Error(result.error.message)
      }

      const quotePublicId = quote.public_id ?? publicId
      if (quotePublicId && CUSTOMER_SHIPPING_NOTIFY_STATUSES.has(next)) {
        const nextOption = getQuoteStatusOption(next)
        const statusText = nextOption
          ? t(nextOption.label)
          : next === DELIVERED_STATUS
            ? t('Delivered', 'Levererad')
            : t('Shipping', 'Frakt')
        await orderNotifications.mutateAsync({
          kind: 'shipping_status',
          actorRole: 'admin',
          subject:
            t('Delivery update for order ', 'Leveransuppdatering för order ') +
            `#${quotePublicId}`,
          shippingStatus: {
            quotePublicId,
            body: `${t('Delivery status', 'Leveransstatus')}: ${statusText}`,
          },
        })
      }

      refetchQuote()
      onUpdated?.(next)
      const nextOption = getQuoteStatusOption(next)
      toast({
        title: nextOption
          ? t(nextOption.label)
          : t('Status updated', 'Status uppdaterad'),
      })
    } catch (error) {
      console.error('Failed to update quote status:', error)
      toast({
        title: t('Could not update status', 'Kunde inte uppdatera status'),
        variant: 'destructive',
      })
    }
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 shrink-0 rounded-full border-gray-200/80 bg-white/90 shadow-lg backdrop-blur-md"
            disabled={updateQuote.isPending}
            aria-label={t('Set status', 'Sätt status')}
          >
            <Settings className="h-3.5 w-3.5" />
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="start" className="w-56">
          {ADMIN_QUOTE_STATUS_GROUPS.map((group, groupIndex) => (
            <div key={group.key}>
              {groupIndex > 0 && <DropdownMenuSeparator />}
              <DropdownMenuLabel className="text-xs font-medium text-muted-foreground">
                {t(group.label)}
              </DropdownMenuLabel>
              {group.statuses.map((value) => {
                const option = getQuoteStatusOption(value)
                if (!option) return null
                const isActive = current === value
                return (
                  <DropdownMenuItem
                    key={value}
                    className="cursor-pointer"
                    disabled={updateQuote.isPending || isActive}
                    onClick={() => void handleChange(value)}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-3.5 w-3.5 shrink-0',
                        isActive ? 'opacity-100' : 'opacity-0'
                      )}
                      strokeWidth={2.5}
                      aria-hidden
                    />
                    {t(option.label)}
                  </DropdownMenuItem>
                )
              })}
            </div>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="cursor-pointer"
            onSelect={() => setOwnershipOpen(true)}
          >
            <ArrowRightLeft className="mr-2 h-3.5 w-3.5 shrink-0" />
            {t('Change ownership', 'Byt ägarskap')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {publicId ? (
        <ChangeOwnershipDialog
          open={ownershipOpen}
          onOpenChange={setOwnershipOpen}
          publicId={publicId}
          currentOrgName={quote.organization?.name}
          onSuccess={() => refetchQuote()}
        />
      ) : null}
    </>
  )
}
